/**
 * EmergencyContacts.js
 * ────────────────────
 * User manages their emergency contacts.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ShieldAlert, ArrowLeft, Plus, Trash2, Mail, Phone, Loader2 } from 'lucide-react';
import { API } from '../utils/apiConfig';

export default function EmergencyContacts() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', email: '', phone: '', relation: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/safety/contacts`);
      setItems(r.data?.contacts || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'load_failed');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError('name_required');
    if (!form.email.trim() && !form.phone.trim()) return setError('email_or_phone_required');
    setBusy(true);
    setError('');
    try {
      const r = await axios.post(`${API}/safety/contacts`, form);
      setItems(prev => [r.data.contact, ...prev]);
      setForm({ name: '', email: '', phone: '', relation: '' });
    } catch (e) {
      setError(e?.response?.data?.detail || 'save_failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t('contacts.confirmDelete', 'Kontakt wirklich entfernen?'))) return;
    try {
      await axios.delete(`${API}/safety/contacts/${id}`);
      setItems(prev => prev.filter(c => c.id !== id));
    } catch {}
  };

  return (
    <div data-testid="emergency-contacts-page" style={st.page}>
      <div style={st.header}>
        <button data-testid="contacts-back" onClick={() => navigate('/dashboard')} style={st.backBtn}>
          <ArrowLeft size={16} /> {t('common.back', 'Zurück')}
        </button>
        <h1 style={st.title}>
          <ShieldAlert size={20} color="#dc2626" /> {t('contacts.title', 'Notfallkontakte')}
        </h1>
      </div>

      <p style={st.lead}>
        {t('contacts.lead',
          'Diese Personen werden per E-Mail benachrichtigt, wenn du den Notruf-Knopf drückst — inklusive deinem Live-Standort.')}
      </p>

      <form onSubmit={submit} style={st.form} data-testid="contacts-form">
        <input
          data-testid="contact-name"
          placeholder={t('contacts.namePlaceholder', 'Name')}
          value={form.name}
          onChange={e => setForm({ ...form, name: e.target.value })}
          style={st.input}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <input
            data-testid="contact-email"
            type="email"
            placeholder={t('contacts.emailPlaceholder', 'E-Mail')}
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            style={st.input}
          />
          <input
            data-testid="contact-phone"
            placeholder={t('contacts.phonePlaceholder', 'Telefon (opt.)')}
            value={form.phone}
            onChange={e => setForm({ ...form, phone: e.target.value })}
            style={st.input}
          />
        </div>
        <input
          data-testid="contact-relation"
          placeholder={t('contacts.relationPlaceholder', 'Beziehung (z. B. Mutter)')}
          value={form.relation}
          onChange={e => setForm({ ...form, relation: e.target.value })}
          style={st.input}
        />
        <button data-testid="contact-add" type="submit" disabled={busy} style={st.addBtn}>
          {busy ? <Loader2 size={16} className="spin" /> : <Plus size={16} />}
          {t('contacts.add', 'Hinzufügen')}
        </button>
      </form>

      {error && <div style={st.error} data-testid="contacts-error">{error}</div>}

      {loading ? (
        <div style={st.empty}><Loader2 size={18} className="spin" /></div>
      ) : items.length === 0 ? (
        <div style={st.empty} data-testid="contacts-empty">
          {t('contacts.empty', 'Noch keine Notfallkontakte hinterlegt.')}
        </div>
      ) : (
        <div style={st.list}>
          {items.map(c => (
            <div key={c.id} style={st.card} data-testid={`contact-${c.id}`}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={st.cardName}>{c.name}
                  {c.relation && <span style={st.rel}>· {c.relation}</span>}
                </div>
                {c.email && <div style={st.cardLine}><Mail size={12} /> {c.email}</div>}
                {c.phone && <div style={st.cardLine}><Phone size={12} /> {c.phone}</div>}
              </div>
              <button data-testid={`contact-${c.id}-delete`} onClick={() => remove(c.id)} style={st.delBtn}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const st = {
  page: { minHeight: '100vh', background: 'linear-gradient(135deg,#0b1220,#020617)', padding: '24px 16px', color: '#f1f5f9' },
  header: { maxWidth: 640, margin: '0 auto 14px', display: 'flex', alignItems: 'center', gap: 12 },
  backBtn: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '6px 10px', color: '#cbd5e1', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 },
  title: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 20, margin: 0 },
  lead: { maxWidth: 640, margin: '0 auto 18px', color: '#94a3b8', fontSize: 14, lineHeight: 1.5 },
  form: { maxWidth: 640, margin: '0 auto 16px', display: 'flex', flexDirection: 'column', gap: 8, padding: 14, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14 },
  input: { padding: '10px 12px', borderRadius: 10, background: 'rgba(2,6,23,0.55)', color: '#f1f5f9', border: '1px solid rgba(255,255,255,0.1)', fontSize: 14, outline: 'none' },
  addBtn: { padding: '10px 14px', borderRadius: 10, background: 'linear-gradient(135deg,#22c55e,#16a34a)', color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 },
  error: { maxWidth: 640, margin: '0 auto 12px', padding: 10, borderRadius: 10, background: 'rgba(239,68,68,0.12)', color: '#fca5a5', fontSize: 13 },
  list: { maxWidth: 640, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 8 },
  card: { display: 'flex', alignItems: 'center', gap: 12, padding: 14, background: 'rgba(15,23,42,0.78)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14 },
  cardName: { fontSize: 15, fontWeight: 600 },
  rel: { color: '#94a3b8', fontWeight: 400, marginLeft: 6, fontSize: 13 },
  cardLine: { fontSize: 12, color: '#cbd5e1', marginTop: 4, display: 'flex', alignItems: 'center', gap: 5 },
  delBtn: { background: 'rgba(239,68,68,0.15)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.35)', borderRadius: 10, padding: 8, cursor: 'pointer' },
  empty: { maxWidth: 640, margin: '40px auto', textAlign: 'center', color: '#64748b', fontSize: 14, display: 'flex', justifyContent: 'center', gap: 8 },
};
